import { FC, useEffect, useState, useMemo } from 'react';
import { CreateLeadCapturePageModal } from '@/features/sales/components/CreateLeadCapturePageModal';
import { Button, PageWrapper, TabList } from '@/components/ui';
import { Plus, Users, Mail, Calendar, Settings, Target, FileText } from 'lucide-react';
import { LeadMagnetTab } from '@/features/marketing/components/LeadMagnetTab';
import {
  TargetsTab,
  PitchesTab,
  SxmLeadsTab,
  SxmOutreachTab,
  OutreachTab,
  SxmAppointmentsTab,
  TargetModal,
  PitchModal,
  ConfigurationTab,
  targetToLabel,
  type Target as TargetType,
  type Pitch,
  type SxmOutreachLog,
} from '@/features/sales';
import {
  useSxmTargets,
  useSxmPitches,
  useAutomationUserContext,
  useSxmLeads,
  useSxmOutreach,
  useSxmBookings
} from '@/hooks';
import { useLeadMagnet } from '@/features/marketing/hooks';


const Sales: FC = () => {
  const {
    effectiveUserId,
    isInternalUser,
    targetUserId,
  } = useAutomationUserContext();


  const [activeTab, setActiveTab] = useState<string>('leads');


  // Set default tab based on role once context is available
  useEffect(() => {
    setActiveTab(isInternalUser ? 'leads' : 'targets');
  }, [isInternalUser]);

  // SXM Data Hooks - These handle internal/external logic internally
  const sxmTargets = useSxmTargets(activeTab === 'targets');
  const sxmPitches = useSxmPitches(activeTab === 'pitches');
  const sxmLeads = useSxmLeads(activeTab === 'leads');
  // Pagination for outreach
  const [outreachPage, setOutreachPage] = useState(1);
  const OUTREACH_LIMIT = 10;

  const sxmOutreach = useSxmOutreach(
    activeTab === 'outreach',
    outreachPage,
    OUTREACH_LIMIT
  );

  // Pagination for appointments
  const [appointmentsPage, setAppointmentsPage] = useState(1);
  const APPOINTMENTS_LIMIT = 10;

  const sxmBookings = useSxmBookings(
    activeTab === 'appointments',
    appointmentsPage,
    APPOINTMENTS_LIMIT
  );

  const handleAppointmentsPageChange = (newPage: number) => {
    setAppointmentsPage(newPage);
  };

  // Lead Magnet Hook (Marketing leads)
  const leadMagnet = useLeadMagnet({
    enabled: activeTab === 'lead-capture' || (isInternalUser && activeTab === 'leads'),
    userId: effectiveUserId,
  });

  const [isLeadCaptureModalOpen, setIsLeadCaptureModalOpen] = useState(false);

  // Computed options from API data
  const targetOptions = useMemo(() => {
    return sxmTargets.targets.map((t) => targetToLabel(t as TargetType));
  }, [sxmTargets.targets]);


  // Transform API data for components
  const targets = useMemo(() => sxmTargets.targets as TargetType[], [sxmTargets.targets]);
  const pitches = useMemo(() => sxmPitches.pitches.map(p => ({
    ...p,
    targetLabel: p.target ? targetToLabel(p.target as TargetType) : ''
  })) as Pitch[], [sxmPitches.pitches]);


  // Modal States
  const [isTargetModalOpen, setTargetModalOpen] = useState(false);
  const [editingTargetId, setEditingTargetId] = useState<number | null>(null);
  const [currentTarget, setCurrentTarget] = useState<TargetType | undefined>();

  const [isPitchModalOpen, setPitchModalOpen] = useState(false);
  const [editingPitchId, setEditingPitchId] = useState<number | null>(null);
  const [currentPitch, setCurrentPitch] = useState<Pitch | undefined>();


  // Handlers
  const handleAddTarget = () => {
    setEditingTargetId(null);
    setCurrentTarget(undefined);
    setTargetModalOpen(true);
  };

  const handleEditTarget = (index: number) => {
    const target = targets[index];
    setEditingTargetId(target.id || null);
    setCurrentTarget(target);
    setTargetModalOpen(true);
  };

  const handleSaveTarget = async (target: TargetType) => {
    const dto = {
      name: target.name || targetToLabel(target),
      role: target.role,
      industry: target.industry,
      country: target.country,
      language: target.language,
    };
    if (editingTargetId === null) await sxmTargets.createTarget(dto);
    else await sxmTargets.updateTarget(editingTargetId, dto);
    setTargetModalOpen(false);
  };

  const handleAddPitch = () => {
    setEditingPitchId(null);
    setCurrentPitch(undefined);
    setPitchModalOpen(true);
  };

  const handleSavePitch = async (pitch: Pitch) => {
    const targetId = targets.find(t => targetToLabel(t) === pitch.targetLabel)?.id;
    const dto = { targetId: targetId || pitch.targetId, strategy: pitch.strategy };
    if (editingPitchId === null) await sxmPitches.createPitch(dto);
    else await sxmPitches.updatePitch(editingPitchId, dto);
    setPitchModalOpen(false);
  };



  // Tabs Definition
  const tabs = useMemo(() => {
    // Internal/Admin users get specific management tabs
    if (isInternalUser) {
      return [
        { id: 'leads', label: 'Leads', icon: <Users className="w-4 h-4" /> },
        { id: 'targets', label: 'Sales Target', icon: <Target className="w-4 h-4" /> },
        { id: 'outreach', label: 'Outreach', icon: <Mail className="w-4 h-4" /> },
        { id: 'appointments', label: 'Appointments', icon: <Calendar className="w-4 h-4" /> },
        { id: 'lead-capture', label: 'Lead Capture', icon: <FileText className="w-4 h-4" /> },
      ];
    }

    // External users get their original functional configuration suite
    return [
      { id: 'targets', label: 'Sales Target', icon: <Target className="w-4 h-4" /> },
      { id: 'appointments', label: 'Appointments', icon: <Calendar className="w-4 h-4" /> },
      { id: 'lead-capture', label: 'Lead Capture', icon: <FileText className="w-4 h-4" /> },
      { id: 'configuration', label: 'Configuration', icon: <Settings className="w-4 h-4" /> },
    ];
  }, [isInternalUser]);


  return (
    <PageWrapper
      title="Sales Automation"
      subtitle="Manage leads, outreach campaigns, and automated meeting scheduling."
      tabs={
        <TabList
          tabs={tabs}
          activeTab={activeTab}
          onChange={setActiveTab}
          className="border-none"
        />
      }
    >
      <div className="mt-6">
        {isInternalUser && !targetUserId ? (
          <div className="flex flex-col items-center justify-center py-20 bg-white border border-dashed border-gray-300 rounded-xl">
            <Users className="w-12 h-12 text-gray-300 mb-4" />
            <h3 className="text-lg font-medium text-gray-900">No User Selected</h3>
            <p className="text-sm text-gray-500 mt-1">Please select an external user from the sidebar to view their sales data.</p>
          </div>
        ) : (
          <>
            {activeTab === 'leads' && (
              isInternalUser ? (
                <div className="space-y-4">
                  <div className="flex justify-between items-center px-1">
                    <div>
                      <h2 className="text-xl font-bold text-gray-900">Leads Management</h2>
                      <p className="text-sm text-gray-500 mt-1">View and manage marketing captured leads.</p>
                    </div>
                  </div>
                  <LeadMagnetTab
                    data={leadMagnet.leads}
                    loading={leadMagnet.leadsLoading}
                    error={leadMagnet.leadsError}
                    pagination={leadMagnet.pagination}
                    onPaginationChange={leadMagnet.handlePaginationChange}
                    onGlobalSearch={leadMagnet.handleLeadSearch}
                    globalFilterValue={leadMagnet.leadSearch}
                    onRefresh={leadMagnet.refresh}
                    userId={effectiveUserId}
                  />
                </div>
              ) : (
                <SxmLeadsTab
                  leads={sxmLeads.leads}
                  loading={sxmLeads.loading}
                  error={sxmLeads.error}
                  userId={effectiveUserId}
                  updateLead={sxmLeads.updateLead}
                  bulkUpdateLeads={sxmLeads.bulkUpdateLeads}
                  deleteLead={sxmLeads.deleteLead}
                  createLead={sxmLeads.createLead}
                />
              )
            )}

            {activeTab === 'outreach' && (
              isInternalUser ? (
                <SxmOutreachTab
                  outreach={sxmOutreach.outreach as unknown as SxmOutreachLog[]}
                  loading={sxmOutreach.loading}
                  error={sxmOutreach.error}
                  userId={effectiveUserId}
                  pagination={{
                    pageIndex: outreachPage - 1,
                    pageSize: OUTREACH_LIMIT,
                    totalCount: sxmOutreach.total,
                  }}
                  onPaginationChange={({ pageIndex }: { pageIndex: number }) => setOutreachPage(pageIndex + 1)}
                />
              ) : (
                <OutreachTab
                  outreach={sxmOutreach.outreach}
                  loading={sxmOutreach.loading}
                  error={sxmOutreach.error}
                  userId={effectiveUserId}
                  pagination={{
                    pageIndex: outreachPage - 1,
                    pageSize: OUTREACH_LIMIT,
                    totalCount: sxmOutreach.total,
                  }}
                  onPaginationChange={({ pageIndex }: { pageIndex: number }) => setOutreachPage(pageIndex + 1)}
                />
              )
            )}

            {activeTab === 'targets' && (
              <TargetsTab
                targets={targets}
                onAdd={handleAddTarget}
                onEdit={handleEditTarget}
                onDelete={(idx) => targets[idx].id && sxmTargets.deleteTarget(targets[idx].id!)}
              />
            )}

            {activeTab === 'pitches' && (
              <PitchesTab
                pitches={pitches}
                onAdd={handleAddPitch}
                onEdit={(idx) => {
                  const p = pitches[idx];
                  setEditingPitchId(p.id || null);
                  setCurrentPitch(p);
                  setPitchModalOpen(true);
                }}
                onDelete={(idx) => pitches[idx].id && sxmPitches.deletePitch(pitches[idx].id!)}
              />
            )}


            {activeTab === 'lead-capture' && (
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <h2 className="text-xl font-bold text-gray-900">Lead Capture Pages</h2>
                  <Button onClick={() => setIsLeadCaptureModalOpen(true)}>
                    <Plus className="mr-2 h-4 w-4" />
                    Create Page
                  </Button>
                </div>
                <LeadMagnetTab
                  data={leadMagnet.leads}
                  loading={leadMagnet.leadsLoading}
                  error={leadMagnet.leadsError}
                  pagination={leadMagnet.pagination}
                  onPaginationChange={leadMagnet.handlePaginationChange}
                  onGlobalSearch={leadMagnet.handleLeadSearch}
                  globalFilterValue={leadMagnet.leadSearch}
                  onRefresh={leadMagnet.refresh}
                  userId={effectiveUserId}
                />
                <CreateLeadCapturePageModal
                  open={isLeadCaptureModalOpen}
                  onOpenChange={setIsLeadCaptureModalOpen}
                  userId={effectiveUserId}
                />
              </div>
            )}

            {activeTab === 'appointments' && (
              <SxmAppointmentsTab
                bookings={sxmBookings.bookings}
                totalBookings={sxmBookings.total}
                currentPage={appointmentsPage}
                itemsPerPage={APPOINTMENTS_LIMIT}
                onPageChange={handleAppointmentsPageChange}
                loading={sxmBookings.loading}
                error={sxmBookings.error}
                userId={effectiveUserId}
                cancelBooking={sxmBookings.cancelBooking}
              />
            )}

            {activeTab === 'configuration' && (
              <ConfigurationTab />
            )}
          </>
        )}
      </div>

      <TargetModal
        isOpen={isTargetModalOpen}
        onClose={() => setTargetModalOpen(false)}
        onSave={handleSaveTarget}
        target={currentTarget}
        mode={editingTargetId === null ? 'create' : 'edit'}
      />

      <PitchModal
        isOpen={isPitchModalOpen}
        onClose={() => setPitchModalOpen(false)}
        onSave={handleSavePitch}
        pitch={currentPitch}
        mode={editingPitchId === null ? 'create' : 'edit'}
        targetOptions={targetOptions}
      />

    </PageWrapper>
  );
};

export default Sales;
